import type { ReactNode } from 'react';
import { LayoutDashboard, LogOut, UserRound } from 'lucide-react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/cn';
import { Avatar } from './Avatar';
import { Button } from './Button';
import { LogoLink } from './Logo';

export function AppShell({
  children,
  wide = false,
  bare = false,
}: {
  children: ReactNode;
  wide?: boolean;
  bare?: boolean;
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#07080b]/80 backdrop-blur-xl">
        <div
          className={cn(
            'mx-auto flex h-16 items-center justify-between px-4 sm:px-6',
            wide ? 'max-w-[1600px]' : 'max-w-7xl',
          )}
        >
          <div className="flex items-center gap-8">
            <LogoLink />
            <nav className="hidden items-center gap-1 sm:flex">
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  cn(
                    'inline-flex h-8 items-center gap-2 rounded-lg px-3 text-[13px] transition-colors',
                    isActive
                      ? 'bg-white/[0.06] text-white'
                      : 'text-zinc-500 hover:bg-white/[0.035] hover:text-zinc-200',
                  )
                }
              >
                <LayoutDashboard className="size-4" />
                Кабинет
              </NavLink>
              <NavLink
                to="/profile"
                className={({ isActive }) =>
                  cn(
                    'inline-flex h-8 items-center gap-2 rounded-lg px-3 text-[13px] transition-colors',
                    isActive
                      ? 'bg-white/[0.06] text-white'
                      : 'text-zinc-500 hover:bg-white/[0.035] hover:text-zinc-200',
                  )
                }
              >
                <UserRound className="size-4" />
                Профиль
              </NavLink>
            </nav>
          </div>

          {user && (
            <div className="flex items-center gap-3">
              <Link
                to="/profile"
                className="hidden items-center gap-2.5 rounded-xl px-1.5 py-1 transition-colors hover:bg-white/[0.04] sm:flex"
              >
                <Avatar name={user.displayName} size="sm" />
                <span className="max-w-40 truncate text-[13px] text-zinc-300">
                  {user.displayName}
                </span>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={onLogout}
                aria-label="Выйти из аккаунта"
                title="Выйти"
              >
                <LogOut className="size-4" />
              </Button>
            </div>
          )}
        </div>
      </header>

      <main
        className={cn(
          'mx-auto w-full px-4 py-8 sm:px-6',
          wide ? 'max-w-[1600px]' : 'max-w-7xl',
          bare && 'p-0 sm:p-0',
        )}
      >
        {children}
      </main>
    </div>
  );
}
